import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';

const Home = () => {
  const navigate = useNavigate();
  const carouselTexts = [
    "Simple & Reliable",
    "Tradition & Innovation", 
  ];

  // Duplicate the texts for seamless looping
  const duplicatedTexts = [...carouselTexts, ...carouselTexts];

  const pppRef = useRef(null);
  const [pppInView, setPppInView] = useState(false);
  
  // Add refs and state for impact section animation
  const impactRef = useRef(null);
  const [impactInView, setImpactInView] = useState(false);
  
  // Add refs and state for products section animation
  const productsRef = useRef(null);
  const [productsInView, setProductsInView] = useState(false);

  // Add refs and state for Who We Are section animation
  const whoWeAreRef = useRef(null);
  const [whoWeAreInView, setWhoWeAreInView] = useState(false);

  // Add state for PPP text slide animation
  const [pppSlideDirection, setPppSlideDirection] = useState('left');
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const el = pppRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPppInView(entry.isIntersecting),
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Add intersection observer for impact section
  useEffect(() => {
    const el = impactRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setImpactInView(entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Add intersection observer for products section
  useEffect(() => {
    const el = productsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setProductsInView(entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Add intersection observer for Who We Are section
  useEffect(() => {
    const el = whoWeAreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setWhoWeAreInView(entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Add scroll listener for PPP slide animation
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine slide direction based on scroll direction
      if (currentScrollY > lastScrollY) {
        setPppSlideDirection('left'); // Scrolling down - slide from left
      } else {
        setPppSlideDirection('right'); // Scrolling up - slide from right
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <div className="overflow-x-hidden">
      <PageTitle title="Home" />
      {/* Hero Section */}
      <div className="relative min-h-screen">
        {/* Background Video */}
        <div className="absolute inset-0 overflow-hidden">
        <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            style={{ minHeight: '100vh' }}
            onError={(e) => console.error('Video failed to load', e)}
          >
            <source src="/hero-video.mp4?v=2" type="video/mp4" />
          </video>
        </div>
        
        {/* Enhanced semi-transparent overlay for better text visibility */}
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        
        {/* Navbar overlay - positioned directly over the video */}
        <div className="absolute top-0 left-0 right-0 z-50">
          <Navbar />
        </div>

        {/* Hero Content */}
        <div className="relative z-40 flex items-center min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl">
              {/* Pre-headline */}
              <p 
                className="text-lg font-medium mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', color: '#ACAD96' }}
              >
                SPINNING THE FUTURE
              </p>
              
              {/* Main Headline */}
              <h1 
                className="text-white text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6"
                style={{ fontFamily: 'Belleza, serif' }}
              >
                Innovative Blends,<br />
                Purposeful Textiles.
              </h1>
              
              {/* Description */}
              <p 
                className="text-white text-lg md:text-xl leading-relaxed max-w-xl"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Crafting niche, high-value yarns that safeguard people, respect the planet, and strengthen industries.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Who We Are Section */}
      <div ref={whoWeAreRef} className="relative bg-white padding-responsive overflow-visible">
        {/* Ribbon decorative element in top right corner - Positioned at absolute top */}
        <div className="absolute -top-12 sm:-top-16 md:-top-18 right-0 w-64 sm:w-72 md:w-80 lg:w-96 h-64 sm:h-72 md:h-80 lg:h-96 z-[9999] pointer-events-none">
          <img 
            src="/Vector 4.png"
            alt="Decorative ribbon"
            className="w-full h-full object-contain object-right-bottom opacity-60 filter brightness-75"
            style={{ transform: 'scale(0.8)', transformOrigin: 'bottom right' }}
          />
        </div>
        
        <div className="container-responsive relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Image */}
            <div className="order-2 lg:order-1">
              <div className="relative flex justify-center lg:justify-end pr-0 lg:pr-6">
                <img 
                  src="/WhatsApp Image 2025-10-21 at 15.23.20_6194b6b0.jpg" 
                  alt="HIK Textiles Industrial Facility"
                  className="w-[85%] h-auto rounded-[24px] shadow-lg"
                />
              </div>
            </div>

            {/* Right Column - Text Content */}
            <div className="order-1 lg:order-2 flex items-center">
              <div className="w-full">
                {/* Heading group (full width) */}
                <div>
                  <h2 
                    className="text-dark text-[40px] leading-[52px] font-normal capitalize pb-2"
                    style={{ fontFamily: 'Belleza, serif' }}
                  >
                    Who We Are
                  </h2>

                  {/* Subheading in its own box, not constrained by paragraph width */}
                  <p 
                    className="text-dark text-[26px] font-medium leading-[32px] md:leading-[52px] md:whitespace-nowrap break-words pb-1"
                    style={{ fontFamily: 'Belleza, serif' }}
                  >
                    Founded in 2018 by seasoned textile professionals
                  </p>
                </div>

                {/* Body box (narrower for more text wrapping) */}
                <div className="max-w-[440px]">
                  <p 
                    className="text-dark text-[16px] leading-[180%] font-normal mb-5"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    HIK Textiles was built with a vision to stir innovation and excellence in Pakistan's spinning industry. We craft high-performance and sustainable yarns that meet the evolving needs of global customers. Our purpose goes beyond profit, every yarn crafted at HIK reflects our commitment to{' '}
                    <span 
                      className="font-bold italic underline hover:decoration-primary underline-offset-2 capitalize cursor-pointer relative"
                      style={{ 
                        fontFamily: 'Poppins, sans-serif',
                        color: '#272525',
                        fontWeight: '600',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#ACAD96';
                        e.target.style.fontWeight = '700';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#272525';
                        e.target.style.fontWeight = '600';
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          transform: whoWeAreInView 
                            ? 'translateX(0)' 
                            : (pppSlideDirection === 'left' ? 'translateX(-100%)' : 'translateX(100%)'),
                          transition: 'transform 0.8s ease-in-out',
                          opacity: whoWeAreInView ? 1 : 0
                        }}
                      >
                        People
                      </span>
                    </span>,{' '}
                    <span 
                      className="font-bold italic underline hover:decoration-primary underline-offset-2 capitalize cursor-pointer relative"
                      style={{ 
                        fontFamily: 'Poppins, sans-serif',
                        color: '#272525',
                        fontWeight: '600',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#ACAD96';
                        e.target.style.fontWeight = '700';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#272525';
                        e.target.style.fontWeight = '600';
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          transform: whoWeAreInView 
                            ? 'translateX(0)' 
                            : (pppSlideDirection === 'left' ? 'translateX(-100%)' : 'translateX(100%)'),
                          transition: 'transform 0.8s ease-in-out 0.2s',
                          opacity: whoWeAreInView ? 1 : 0
                        }}
                      >
                        Planet
                      </span>
                    </span>,{' '}
                    &amp; <span 
                      className="font-bold italic underline hover:decoration-primary underline-offset-2 capitalize cursor-pointer relative"
                      style={{ 
                        fontFamily: 'Poppins, sans-serif',
                        color: '#272525',
                        fontWeight: '600',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#ACAD96';
                        e.target.style.fontWeight = '700';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#272525';
                        e.target.style.fontWeight = '600';
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          transform: whoWeAreInView 
                            ? 'translateX(0)' 
                            : (pppSlideDirection === 'left' ? 'translateX(-100%)' : 'translateX(100%)'),
                          transition: 'transform 0.8s ease-in-out 0.4s',
                          opacity: whoWeAreInView ? 1 : 0
                        }}
                      >
                        Performance
                      </span>
                    </span>.
                  </p>

                  <button 
                    className="bg-primary text-white px-6 py-2 rounded-md text-sm font-semibold hover:bg-opacity-90 transition-all block mx-auto lg:mx-0"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                    onClick={() => navigate('/get-in-touch')}
                  >
                    Get in Touch
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Products Section */}
      <div ref={productsRef} className="relative py-20">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/Rectangle 18.png')",
            minHeight: '100%'
          }}
        />
        
        <div className="relative z-10">
          {/* Header Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
            <div className="text-left">
              <p 
                className="our-products-label text-[16px] uppercase font-medium leading-[100%] mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', color: '#ACAD96' }}
              >
                OUR PRODUCTS
              </p>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                <h2 
                  className="our-products-title text-left font-normal text-[32px] leading-[130%] capitalize flex-1 md:mr-[100px] mb-4 md:mb-0"
                  style={{ 
                    fontFamily: 'Belleza, serif', 
                    color: '#272525'
                  }}
                >
                  High-Quality Textile Yarns for Hosiery, Casual Wear, Kids Wear, Carpet Industry, Knitting and Weaving, Outdoor and Sports Industry With expertise in developing yarns tailored specifically for your needs
                </h2>
                <button 
                  className="px-6 py-3 rounded-md text-[16px] font-semibold hover:opacity-90 transition-all whitespace-nowrap self-center md:self-start mt-8"
                  style={{ 
                    fontFamily: 'Belleza, serif',
                    fontWeight: '600',
                    fontSize: '16px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    color: '#FFFFFF',
                    backgroundColor: '#ACAD96'
                  }}
                  onClick={() => navigate('/products')}
                >
                  Explore Our Products
                </button>
              </div>
            </div>
          </div>

          {/* Product Categories Grid with Animated Borders */}
          <div className="mb-16">
            {/* Animated Border Container */}
            <div className="w-full bg-transparent relative">
              {/* Top border - animates from left edge */}
              <div 
                className="absolute top-0 left-0 h-0.5 bg-gray-500/80"
                style={{
                  width: productsInView ? '100%' : '0%',
                  transition: 'width 1.5s ease-in-out 0.2s'
                }}
              />
              
              {/* Bottom border - animates from right edge */}
              <div 
                className="absolute bottom-0 right-0 h-0.5 bg-gray-500/80"
                style={{
                  width: productsInView ? '100%' : '0%',
                  transition: 'width 1.5s ease-in-out 0.4s'
                }}
              />
              
              {/* Centered content inside */}
              <div className="max-w-7xl mx-auto px-0">
                {/* Grid container with animated side borders */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 mx-4 md:mx-6 relative">
                  
                  {/* Left border - animates from top */}
                  <div 
                    className="absolute left-0 top-0 w-0.5 bg-gray-500/80"
                    style={{
                      height: productsInView ? '100%' : '0%',
                      transition: 'height 1.5s ease-in-out 0.6s'
                    }}
                  />
                  
                  {/* Right border - animates from bottom */}
                  <div 
                    className="absolute right-0 bottom-0 w-0.5 bg-gray-500/80"
                    style={{
                      height: productsInView ? '100%' : '0%',
                      transition: 'height 1.5s ease-in-out 0.8s'
                    }}
                  />
                  
                  {/* Mobile: Horizontal dividers between boxes */}
                  <div 
                    className="absolute w-full h-0.5 bg-gray-500/80 md:hidden"
                    style={{
                      top: '25%',
                      left: '0',
                      width: productsInView ? '100%' : '0%',
                      transition: 'width 1.2s ease-in-out 1.0s'
                    }}
                  />
                  <div 
                    className="absolute w-full h-0.5 bg-gray-500/80 md:hidden"
                    style={{
                      top: '50%',
                      left: '0',
                      width: productsInView ? '100%' : '0%',
                      transition: 'width 1.2s ease-in-out 1.2s'
                    }}
                  />
                  <div 
                    className="absolute w-full h-0.5 bg-gray-500/80 md:hidden"
                    style={{
                      top: '75%',
                      left: '0',
                      width: productsInView ? '100%' : '0%',
                      transition: 'width 1.2s ease-in-out 1.4s'
                    }}
                  />
                  
                  {/* Desktop: Vertical dividers */}
                  <div 
                    className="absolute w-0.5 bg-gray-500/80 hidden md:block"
                    style={{
                      left: '25%',
                      height: productsInView ? '100%' : '0%',
                      transition: 'height 1.2s ease-in-out 1.0s'
                    }}
                  />
                  <div 
                    className="absolute w-0.5 bg-gray-500/80 hidden md:block"
                    style={{
                      left: '50%',
                      bottom: '0',
                      height: productsInView ? '100%' : '0%',
                      transition: 'height 1.2s ease-in-out 1.2s'
                    }}
                  />
                  <div 
                    className="absolute w-0.5 bg-gray-500/80 hidden md:block"
                    style={{
                      left: '75%',
                      height: productsInView ? '100%' : '0%',
                      transition: 'height 1.2s ease-in-out 1.4s'
                    }}
                  />

                  {/* Cotton */}
                  <div className="flex flex-col items-start h-full pl-10 pr-8 py-24 text-left relative z-10">
                    <img
                      src="/Mask group (2).png"
                      alt="Cotton icon"
                      className="w-12 h-12 object-contain mb-4"
                    />
                    <h3 
                      className="whitespace-nowrap text-[20px] font-semibold mb-2"
                      style={{ fontFamily: 'Belleza, serif', color: '#272525' }}
                    >
                      Apparel Yarns
                    </h3>
                    <p 
                      className="text-[16px] leading-[100%] font-normal"
                      style={{ fontFamily: 'Poppins, sans-serif', color: '#272525' }}
                    >
                      Yarns for men's wear, blending fibers for drape, comfort, and performance.
                    </p>
                  </div>

                  {/* Polyester Yarns */}
                  <div className="flex flex-col items-start h-full pl-10 pr-8 py-24 text-left relative z-10">
                    <img
                      src="/Mask group (3).png"
                      alt="Polyester icon"
                      className="w-12 h-12 object-contain mb-4"
                    />
                    <h3 
                      className="whitespace-nowrap text-[20px] font-semibold mb-2"
                      style={{ fontFamily: 'Belleza, serif', color: '#272525' }}
                    >
                      Home & Interior Yarns
                    </h3>
                    <p 
                      className="text-[16px] leading-[100%] font-normal"
                      style={{ fontFamily: 'Poppins, sans-serif', color: '#272525' }}
                    >
                      Yarns for home textiles, focused on color fastness, strength, and easy care.
                    </p>
                  </div>

                  {/* Blended Yarns */}
                  <div className="flex flex-col items-start h-full pl-10 pr-8 py-24 text-left relative z-10">
                    <img
                      src="/Mask group (4).png"
                      alt="Blended icon"
                      className="w-12 h-12 object-contain mb-4"
                    />
                    <h3 
                      className="whitespace-nowrap text-[20px] font-semibold mb-2"
                      style={{ fontFamily: 'Belleza, serif', color: '#272525' }}
                    >
                      Technical & Industrial Yarns
                    </h3>
                    <p 
                      className="text-[16px] leading-[100%] font-normal"
                      style={{ fontFamily: 'Poppins, sans-serif', color: '#272525' }}
                    >
                      Yarns for workwear, automotive, filtration, and other performance applications.
                    </p>
                  </div>

                  {/* Specialty Yarns */}
                  <div className="flex flex-col items-start h-full pl-10 pr-8 py-24 text-left relative z-10">
                    <img
                      src="/Mask group (5).png"
                      alt="Specialty icon"
                      className="w-12 h-12 object-contain mb-4"
                    />
                    <h3 
                      className="whitespace-nowrap text-[20px] font-semibold mb-2"
                      style={{ fontFamily: 'Belleza, serif', color: '#272525' }}
                    >
                      Specialty Yarns
                    </h3>
                    <p 
                      className="text-[16px] leading-[100%] font-normal"
                      style={{ fontFamily: 'Poppins, sans-serif', color: '#272525' }}
                    >
                      Dope-dyed, ring-spun, mélange, and sustainable yarns with unique finishes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Impact Section */}
      <section ref={impactRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3
            className="text-center mt-8 mb-24 text-[40px] leading-[52px] font-normal capitalize"
            style={{ fontFamily: 'Belleza, serif', color: '#272525' }}
          >
            Our Impact
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16 lg:gap-24 text-center">
            {/* 1 */}
            <div>
              <div className="text-[40px] leading-[52px] font-medium mb-0 capitalize" style={{ fontFamily: 'Poppins, sans-serif', color: '#ACAD96F7' }}>
                {impactInView && (
                  <CountUp 
                    end={26415} 
                    duration={2.5} 
                    separator=","
                    enableScrollSpy
                    scrollSpyOnce
                  />
                )}
              </div>
              <div className="mt-3 mb-4 text-[20px] leading-[25px] font-normal text-center capitalize" style={{ fontFamily: 'Poppins, sans-serif', color: '#000000' }}>
                Tonnes Produced<br/> &amp; Delivered
              </div>
            </div>

            {/* 2 */}
            <div>
              <div className="text-[40px] leading-[52px] font-medium mb-0 capitalize" style={{ fontFamily: 'Poppins, sans-serif', color: '#ACAD96F7' }}>
                {impactInView && (
                  <CountUp 
                    end={4030} 
                    duration={2.5} 
                    separator=","
                    enableScrollSpy
                    scrollSpyOnce
                  />
                )}
              </div>
              <div className="mt-3 mb-4 text-[20px] leading-[25px] font-normal text-center capitalize" style={{ fontFamily: 'Poppins, sans-serif', color: '#000000' }}>
                Tonnes Of<br/> Recycled Materials
              </div>
            </div>

            {/* 3 */}
            <div>
              <div className="text-[40px] leading-[52px] font-medium mb-0 capitalize" style={{ fontFamily: 'Poppins, sans-serif', color: '#ACAD96F7' }}>
                {impactInView && (
                  <CountUp 
                    end={39} 
                    duration={2.5} 
                    suffix="+"
                    enableScrollSpy
                    scrollSpyOnce
                  />
                )}
              </div>
              <div className="mt-3 mb-4 text-[20px] leading-[25px] font-normal text-center capitalize" style={{ fontFamily: 'Poppins, sans-serif', color: '#000000' }}>
                Satisfied<br/> Clients
              </div>
            </div>

            {/* 4 */}
            <div>
              <div className="text-[40px] leading-[52px] font-medium mb-0 capitalize" style={{ fontFamily: 'Poppins, sans-serif', color: '#ACAD96F7' }}>
                {impactInView && (
                  <CountUp 
                    end={7} 
                    duration={2.5} 
                    suffix="+"
                    enableScrollSpy
                    scrollSpyOnce
                  />
                )}
              </div>
              <div className="mt-3 mb-20 text-[20px] leading-[25px] font-normal text-center capitalize" style={{ fontFamily: 'Poppins, sans-serif', color: '#000000' }}>
                Years Of<br/> Service
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability section – restructured with separate containers */}
      <section className="relative w-full">
        {/* Top container with headings and logos */}
        <div className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <p
                className="text-[16px] leading-[100%] uppercase font-semibold mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', color: '#ACAD96' }}
              >
                CERTIFICATIONS
              </p>
              <h3
                className="text-[40px] leading-[100%] font-normal capitalize mb-6"
                style={{ fontFamily: 'Belleza, serif', color: '#272525' }}
              >
                Sustainability At HIK
              </h3>
              <p
                className="text-[16px] leading-[100%] font-normal mb-6 md:mb-8"
                style={{ fontFamily: 'Poppins, sans-serif', color: '#272525' }}
              >
                At HIK, a win isn't complete unless it's a win for the planet too.
              </p>
              <div className="flex items-center justify-center gap-6 md:gap-10 lg:gap-14">
                <img src="/image 28.png" alt="Global Recycled Standard" className="h-20 md:h-24 lg:h-28 w-auto object-contain" />
                <img src="/image 29.png" alt="OEKO-TEX" className="h-20 md:h-24 lg:h-28 w-auto object-contain" />
                <img src="/image 26.png" alt="ISO 9001:2015" className="h-20 md:h-24 lg:h-28 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom container with image */}
        <div className="relative w-full">
          <img
            src="/Hompage footer image.jpg"
            alt="Sustainability background"
            className="footer-hero-img w-full h-auto object-contain"
          />
        </div>
      </section>

      {/* Footer */}
      <div className="relative z-50">
        <Footer />
      </div>
        
      {/* Custom CSS for the seamless scrolling animation */}
      <style jsx>{`
        @keyframes seamlessScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        /* Mobile-only font overrides for Our Products section */
        @media (max-width: 640px) {
          .our-products-label { 
            font-family: Belleza, serif !important;
            font-weight: 400 !important;
            font-style: normal !important;
            font-size: 32px !important;
            line-height: 100% !important;
            letter-spacing: 0% !important;
            text-transform: capitalize !important;
            color: #272525 !important;
          }
          .our-products-title { 
            font-family: Poppins, sans-serif !important;
            font-weight: 600 !important;
            font-style: normal !important;
            font-size: 16px !important;
            line-height: 100% !important;
            letter-spacing: 0% !important;
            text-transform: uppercase !important;
            color: #ACAD96 !important;
          }
        }

        /* Mobile: increase height of the image above the footer */
        @media (max-width: 640px) {
          .footer-hero-img {
            height: 60vh;       /* increase as desired (e.g., 50vh–70vh) */
            object-fit: cover;  /* fill height nicely on small screens */
          }
        }
      `}
      </style>
    </div>
  );
};

export default Home;